import React from "react";
import { Link } from "react-router-dom";
import PolicyLayout, { PolicySection } from "./PolicyLayout";

function Terms() {
  return (
    <PolicyLayout
      title="Terms of Service & Sale"
      intro="These terms govern use of the mefurvexo website and purchases made through it. By submitting an order, you agree to the version displayed at that time."
      contactSubject="these terms"
    >
      <PolicySection title="1. Store identity and scope">
        <p>
          In these terms, &quot;mefurvexo&quot;, &quot;we&quot;, and &quot;us&quot; refer to the
          online store identified in the contact section, and &quot;you&quot; means a site
          visitor or customer. Separate policies linked from the{" "}
          <Link to="/policies">Policy Centre</Link> form part of these terms where
          relevant. Mandatory consumer rights always take priority over any
          inconsistent provision.
        </p>
      </PolicySection>

      <PolicySection title="2. Eligibility and accurate information">
        <p>
          You must have legal capacity to make the purchase or act with permission
          of a parent, guardian, or authorised organisation. Information submitted
          for an order must be current and accurate. You may not impersonate
          another person or use a payment method without authority.
        </p>
      </PolicySection>

      <PolicySection title="3. Product information">
        <p>
          We aim to describe material, dimensions, finish, colour, price, and
          availability accurately. Screen settings, lighting, hand-finishing, and
          natural stones or pearls can produce reasonable variations. Measurements
          are approximate unless stated otherwise. Product details at the time of
          order form part of the purchase information.
        </p>
        <p>
          Jewellery is not a toy. Please review our{" "}
          <Link to="/product-care">Product Care &amp; Safety Guide</Link> before use.
        </p>
      </PolicySection>

      <PolicySection title="4. Prices, taxes, and payment">
        <p>
          Prices are shown in the currency displayed by the store. Delivery fees,
          taxes, duties, and other charges are displayed or described before order
          submission where they can be calculated. The payment method may place an
          authorisation hold before capture. Full details appear in the{" "}
          <Link to="/payments">Payment &amp; Pricing Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="5. Order process and acceptance">
        <ol>
          <li>Adding an item to the cart does not reserve stock.</li>
          <li>Submitting payment is an offer to buy the items in the order.</li>
          <li>An automated receipt only confirms that the order was received.</li>
          <li>The sale is accepted when we send an acceptance or dispatch confirmation, unless applicable law provides otherwise.</li>
        </ol>
        <p>
          We may decline or cancel an order before acceptance for genuine reasons,
          including unavailable stock, an obvious price or description error,
          payment failure, suspected fraud, delivery restrictions, or legal
          requirements. Any amount captured for a cancelled order will be returned
          to the original payment method.
        </p>
      </PolicySection>

      <PolicySection title="6. Quantity and resale limits">
        <p>
          Reasonable quantity limits may be applied fairly to protect stock,
          customers, or promotion integrity. Unless agreed in writing, the store is
          intended for personal consumer purchases rather than unauthorised resale.
          We will not use these limits in a way prohibited by law.
        </p>
      </PolicySection>

      <PolicySection title="7. Shipping and delivery">
        <p>
          Available destinations, charges, and estimates are shown at checkout.
          Estimates are not guaranteed delivery dates. Customs processing, weather,
          carrier disruption, and inaccurate address information can cause delays.
          The <Link to="/shipping">Shipping &amp; Delivery Policy</Link> explains
          tracking, import charges, failed delivery, and damage in transit.
        </p>
      </PolicySection>

      <PolicySection title="8. Changes, cancellations, returns, and faults">
        <p>
          Contact us promptly if an order needs to be changed or cancelled; a
          request cannot be guaranteed after processing begins. Change-of-mind
          returns, hygiene exclusions, exchanges, and refund timing are in the{" "}
          <Link to="/returns">Returns &amp; Refunds Policy</Link>. Faulty, damaged, or
          incorrectly supplied goods are addressed in the{" "}
          <Link to="/warranty">Warranty &amp; Faulty Goods Policy</Link>. These policies
          do not limit non-excludable statutory remedies.
        </p>
      </PolicySection>

      <PolicySection title="9. Promotions, gift cards, and store credit">
        <p>
          Offer-specific terms shown with a promotion apply in addition to our{" "}
          <Link to="/promotions">Promotions Policy</Link>. If the store offers gift
          cards or credit, the <Link to="/gift-cards">Gift Cards &amp; Store Credit Policy</Link>
          {" "}also applies. We may correct misuse or technical errors while
          honouring completed purchases and legal rights as required.
        </p>
      </PolicySection>

      <PolicySection title="10. Acceptable use">
        <p>You must not use the website to:</p>
        <ul>
          <li>break a law, infringe another person&apos;s rights, or submit unlawful, deceptive, abusive, or defamatory material;</li>
          <li>introduce malicious code, bypass access controls, disrupt operation, or probe a system without permission;</li>
          <li>scrape, copy, or automate access in a way that burdens the service or violates applicable rights;</li>
          <li>collect another person&apos;s data without a lawful basis; or</li>
          <li>manipulate reviews, promotions, payment disputes, or the order process.</li>
        </ul>
        <p>
          Access may be restricted where reasonably necessary to protect the store,
          customers, or third parties, subject to applicable law.
        </p>
      </PolicySection>

      <PolicySection title="11. Reviews and submissions">
        <p>
          If you send a review, photograph, or other content for publication, you
          confirm you have the necessary rights and that it is accurate and lawful.
          You give us a non-exclusive, worldwide, royalty-free licence to host,
          reproduce, and display that content only for operating, promoting, and
          documenting the store, until removed where reasonably practicable. You
          retain ownership. We may moderate content for relevance, privacy, safety,
          or legal compliance, but will not knowingly alter a review&apos;s substantive
          meaning.
        </p>
      </PolicySection>

      <PolicySection title="12. Intellectual property">
        <p>
          Store text, branding, original graphics, layout, and product imagery are
          owned by or lawfully licensed to their respective rights holders. A
          limited permission is granted to browse and make a personal copy of order
          records. No other licence or transfer of rights is implied. See the{" "}
          <Link to="/intellectual-property">Intellectual Property &amp; Takedown Policy</Link>
          {" "}for permitted use and rights-owner notices.
        </p>
      </PolicySection>

      <PolicySection title="13. Third-party services and links">
        <p>
          Payment, delivery, and linked services may have their own terms. We do not
          control an unrelated website merely because it is linked here. This does
          not reduce responsibility that the law places on us for our own sale or
          selection of a service provider.
        </p>
      </PolicySection>

      <PolicySection title="14. Availability and disclaimers">
        <p>
          We aim to keep the website accurate and available but cannot promise it
          will always be uninterrupted or error-free. Site access may be suspended
          for maintenance, security, or events outside reasonable control. Nothing
          in these terms excludes an express product promise or a warranty,
          guarantee, condition, duty, or remedy that cannot lawfully be excluded.
        </p>
      </PolicySection>

      <PolicySection title="15. Responsibility for loss">
        <p>
          Each party remains responsible for loss it causes according to applicable
          law. To the extent the law allows, we are not responsible for remote or
          unforeseeable loss, or business loss arising from a consumer purchase. We
          do not exclude liability for fraud, deliberate misconduct, death or
          personal injury caused by negligence, or any other liability that cannot
          lawfully be excluded or limited.
        </p>
      </PolicySection>

      <PolicySection title="16. Events outside reasonable control">
        <p>
          Neither party is responsible for delay caused by an event it could not
          reasonably control, provided reasonable steps are taken to reduce the
          effect and resume performance. This clause does not make you pay for goods
          we cancel or fail to supply and does not remove mandatory rights.
        </p>
      </PolicySection>

      <PolicySection title="17. Governing law and disputes">
        <p>
          These terms are governed by the laws of Hong Kong, without preventing a
          consumer from relying on mandatory protections or courts available under
          the law of their habitual residence. Please use the{" "}
          <Link to="/complaints">Complaints &amp; Dispute Resolution</Link> process first
          so we have an opportunity to resolve the issue.
        </p>
      </PolicySection>

      <PolicySection title="18. General provisions and changes">
        <p>
          If one provision is unenforceable, the remaining provisions continue to
          apply. A delay in enforcing a right is not a waiver. You may not transfer
          an order contract without our agreement, but we may transfer it as part of
          a genuine business reorganisation if your rights are not reduced. Updated
          terms apply prospectively from the date posted; the terms accepted for an
          existing order continue to govern that order unless law requires otherwise.
        </p>
      </PolicySection>
    </PolicyLayout>
  );
}

export default Terms;
